import { create } from "zustand";

export type ToastTone = "success" | "error" | "warning" | "info";

export interface ToastAction {
  label: string;
  onClick: () => void | Promise<void>;
}

export interface ToastItem {
  id: string;
  title: string;
  description?: string;
  tone: ToastTone;
  /** Optional primary action (e.g. Undo) rendered in the toast. */
  action?: ToastAction;
  /** Auto-dismiss duration. Default 4200ms. Use longer for undo windows. */
  durationMs?: number;
  /** Show countdown progress bar (typically with an action). */
  progress?: boolean;
  /** Absolute expiry timestamp for progress UI. */
  expiresAt?: number;
}

interface ToastState {
  toasts: ToastItem[];
  push: (toast: Omit<ToastItem, "id" | "expiresAt"> & { id?: string }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const timers = new Map<string, number>();
let seq = 0;

function clearTimer(id: string) {
  const t = timers.get(id);
  if (t) {
    window.clearTimeout(t);
    timers.delete(id);
  }
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push: (toast) => {
    const id = toast.id ?? `toast-${++seq}`;
    clearTimer(id);
    const durationMs = toast.durationMs ?? 4200;
    const expiresAt = Date.now() + durationMs;
    set((s) => ({
      toasts: [
        ...s.toasts.filter((t) => t.id !== id),
        {
          id,
          title: toast.title,
          description: toast.description,
          tone: toast.tone,
          action: toast.action,
          durationMs,
          progress: toast.progress,
          expiresAt,
        },
      ],
    }));
    const timer = window.setTimeout(() => {
      timers.delete(id);
      get().dismiss(id);
    }, durationMs);
    timers.set(id, timer);
    return id;
  },
  dismiss: (id) => {
    clearTimer(id);
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear: () => {
    for (const id of timers.keys()) clearTimer(id);
    set({ toasts: [] });
  },
}));

export function toast(tone: ToastTone, title: string, description?: string) {
  return useToastStore.getState().push({ tone, title, description });
}

export type UndoToastOptions = {
  title: string;
  description?: string;
  durationMs?: number;
  onUndo: () => void | Promise<void>;
};

/**
 * Success toast with an Undo action and countdown progress.
 * Timer expiry only dismisses the toast — it never permanently deletes records.
 */
export function toastWithUndo(options: UndoToastOptions) {
  const durationMs = options.durationMs ?? 10_000;
  let undoing = false;
  const id = `undo-${++seq}`;

  return useToastStore.getState().push({
    id,
    tone: "success",
    title: options.title,
    description: options.description,
    durationMs,
    progress: true,
    action: {
      label: "Undo",
      onClick: async () => {
        if (undoing) return;
        undoing = true;
        try {
          await options.onUndo();
          useToastStore.getState().dismiss(id);
        } catch {
          undoing = false;
          throw new Error("undo-failed");
        }
      },
    },
  });
}
